/**
 * TanStack Query hooks for Sync Run management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import { fetchSyncRuns, fetchSyncRun, cancelSyncRun } from '@/lib/uas-api'

export const syncRunKeys = {
  all: ['uas', 'sync-runs'] as const,
  lists: () => [...syncRunKeys.all, 'list'] as const,
  list: (params: { page?: number; pageSize?: number }) =>
    [...syncRunKeys.lists(), params] as const,
  details: () => [...syncRunKeys.all, 'detail'] as const,
  detail: (id: string) => [...syncRunKeys.details(), id] as const,
}

export function useSyncRuns(params: { page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: syncRunKeys.list(params),
    queryFn: () => fetchSyncRuns(params),
    placeholderData: (previousData) => previousData,
    staleTime: 10 * 1000,
  })
}

export function useSyncRun(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? syncRunKeys.detail(id)
      : ['uas', 'sync-runs', 'detail', 'disabled'],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Sync run ID is required'))
      return fetchSyncRun(id)
    },
    enabled: !!id,
    retry: false,
    refetchInterval: (query) => {
      const run = query.state.data
      if (run && (run.status === 'PENDING' || run.status === 'RUNNING')) {
        return 3000
      }
      return false
    },
  })
}

export function useCancelSyncRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => cancelSyncRun(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: syncRunKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: syncRunKeys.lists() })
      toast.success('Sync run cancelled')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to cancel sync: ${message}`)
    },
  })
}
